/**
 * Address Map Picker (AMP)
 * ─────────────────────────────────────────────
 * Google Maps pin-point address picker.
 *
 * TRIGGER:
 *   Open from any page via URL param:  ?address_map=1
 *   Or programmatically:               window._ampOpen()
 *
 * FLOW:
 *   1. Modal opens (Step 1) — asks GPS or manual
 *   2. GPS: browser asks permission → centers map on user coords
 *      Manual: focuses search input
 *   3. Step 2 — map view: drag pin or search to set location
 *   4. "Save Address" → logs payload + calls backend endpoint
 *
 * BACKEND (prepared, not active):
 *   POST  BACKEND_URL/api/address/save
 *   Body: { lat, lng, formatted, address1, city, province, zip, country, extra }
 */

(function () {
  'use strict';

  /* ── CONFIG ─────────────────────────────────── */
  var GMAPS_API_KEY    = 'AIzaSyBmnh6diibvrDq-nLO5UAXInBBdl9NDJlE';
  var DEFAULT_CENTER   = { lat: -6.2088, lng: 106.8456 }; // Jakarta fallback
  var DEFAULT_ZOOM     = 15;
  var GPS_ZOOM         = 18;   // tighter zoom once we have an accurate GPS fix
  var GPS_SAMPLE_MS    = 7000; // how long to keep refining the GPS reading
  var GPS_GOOD_ACCURACY = 20;  // metres — stop early once we're this precise
  var URL_PARAM        = 'address_map';   // ?address_map=1 opens the modal
  var BACKEND_URL      = 'https://backend-address-minuman.vercel.app'; // TODO: replace with real endpoint

  /* ── STATE ──────────────────────────────────── */
  var map        = null;
  var geocoder   = null;
  var marker     = null;
  var autocomplete = null;
  var mapsLoaded = false;
  var scrollPos  = 0;
  var editMode   = false;   // false = create new address, true = update existing
  var editAddressId = null; // Shopify MailingAddress id when editing
  var selectedAddress = {
    formatted: '', lat: 0, lng: 0,
    extra: '', address1: '', city: '', province: '', zip: '', country: ''
  };

  /* ── HELPERS ────────────────────────────────── */
  function qs(sel, ctx)  { return (ctx || document).querySelector(sel); }

  function lockScroll() {
    scrollPos = window.pageYOffset;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top      = '-' + scrollPos + 'px';
    document.body.style.width    = '100%';
  }

  function unlockScroll() {
    document.body.style.overflow  = '';
    document.body.style.position  = '';
    document.body.style.top       = '';
    document.body.style.width     = '';
    window.scrollTo(0, scrollPos);
  }

  function showToast(msg, type) {
    var t = qs('.amp-toast');
    if (!t) return;
    t.textContent = msg;
    t.className   = 'amp-toast amp-toast--' + (type || 'info') + ' amp-toast--visible';
    clearTimeout(t._timer);
    t._timer = setTimeout(function () {
      t.classList.remove('amp-toast--visible');
    }, 3000);
  }

  /* ── GOOGLE MAPS LOADER ─────────────────────── */
  function loadGoogleMaps() {
    return new Promise(function (resolve, reject) {
      if (mapsLoaded && window.google && window.google.maps) {
        resolve();
        return;
      }
      if (qs('script[data-amp-gmaps]')) {
        // Already loading — wait
        var poll = setInterval(function () {
          if (window.google && window.google.maps) {
            clearInterval(poll);
            mapsLoaded = true;
            resolve();
          }
        }, 100);
        return;
      }
      window._ampGmapsReady = function () {
        mapsLoaded = true;
        resolve();
      };
      var s    = document.createElement('script');
      s.dataset.ampGmaps = '1';
      s.src    = 'https://maps.googleapis.com/maps/api/js?key=' + GMAPS_API_KEY
               + '&libraries=places&callback=_ampGmapsReady';
      s.async  = true;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  /* ── GEOLOCATION ────────────────────────────── */
  // The first GPS fix the browser hands back is usually coarse (cell/wifi based).
  // Instead of taking it, we watch the position for a short window and keep the
  // most accurate reading, stopping early once it's precise enough.
  function getCurrentPosition() {
    return new Promise(function (resolve, reject) {
      if (!navigator.geolocation) { reject(new Error('No geolocation')); return; }

      var best     = null;
      var watchId  = null;
      var settled  = false;

      function finish() {
        if (settled) return;
        settled = true;
        if (watchId !== null) navigator.geolocation.clearWatch(watchId);
        clearTimeout(timer);
        if (best) { resolve(best); }
        else      { reject(new Error('No position acquired')); }
      }

      var timer = setTimeout(finish, GPS_SAMPLE_MS);

      watchId = navigator.geolocation.watchPosition(function (pos) {
        // Keep the reading with the smallest (best) accuracy radius.
        if (!best || pos.coords.accuracy < best.coords.accuracy) {
          best = pos;
        }
        // Good enough — no need to keep draining the GPS.
        if (best.coords.accuracy <= GPS_GOOD_ACCURACY) finish();
      }, function (err) {
        // Only bail out if we never got a single reading.
        if (!best) { settled = true; clearTimeout(timer); reject(err); }
      }, {
        enableHighAccuracy: true,
        timeout:            GPS_SAMPLE_MS,
        maximumAge:         0
      });
    });
  }

  /* ── MODAL HTML ─────────────────────────────── */
  function createModalHTML() {
    return [
      '<div class="amp-overlay" id="ampOverlay" role="dialog" aria-modal="true" aria-label="Delivery Address">',

      /* ── Step 1: GPS or Manual ── */
      '  <div class="amp-modal" id="ampStep1">',
      '    <button class="amp-close" id="ampClose1" aria-label="Close">',
      '      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">',
      '        <path d="M1 1l16 16M17 1L1 17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
      '      </svg>',
      '    </button>',
      '    <div class="amp-step1-body">',
      '      <div class="amp-icon-wrap">',
      '        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">',
      '          <circle cx="12" cy="12" r="10"/>',
      '          <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>',
      '          <circle cx="12" cy="12" r="3"/>',
      '        </svg>',
      '      </div>',
      '      <h2 class="amp-step1-title">Where should we deliver?</h2>',
      '      <p class="amp-step1-desc">Allow us to detect your current location for a faster experience, or search your address manually.</p>',
      '      <button class="amp-btn amp-btn--primary" id="ampGpsBtn">',
      '        <span class="amp-gps-idle">',
      '          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>',
      '          Use My Current Location',
      '        </span>',
      '        <span class="amp-gps-loading" hidden>',
      '          <span class="amp-spinner"></span>',
      '          Detecting location…',
      '        </span>',
      '      </button>',
      '      <button class="amp-btn amp-btn--secondary" id="ampManualBtn">',
      '        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>',
      '        Search Address Manually',
      '      </button>',
      '    </div>',
      '  </div>',

      /* ── Step 2: Map ── */
      '  <div class="amp-modal amp-modal--map" id="ampStep2" hidden>',
      '    <div class="amp-map-header">',
      '      <div class="amp-map-header-top">',
      '        <div class="amp-map-title">',
      '          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>',
      '          Delivery Address',
      '        </div>',
      '        <button class="amp-close" id="ampClose2" aria-label="Close">',
      '          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">',
      '            <path d="M1 1l16 16M17 1L1 17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
      '          </svg>',
      '        </button>',
      '      </div>',
      '      <div class="amp-search-wrap">',
      '        <svg class="amp-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>',
      '        <input id="ampSearchInput" class="amp-search-input" type="text" placeholder="Search your address..." autocomplete="off"/>',
      '        <div id="ampAutocompleteList" class="amp-ac-list" hidden></div>',
      '      </div>',
      '    </div>',

      '    <div class="amp-map-container">',
      '      <div id="ampMapCanvas"></div>',
      '      <button class="amp-recenter-btn" id="ampRecenterBtn" title="Use my location">',
      '        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>',
      '      </button>',
      '      <div class="amp-tap-hint">',
      '        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>',
      '        Tap on the map to set your exact location',
      '      </div>',
      '    </div>',

      '    <div class="amp-address-panel">',
      '      <div class="amp-selected-label">SELECTED ADDRESS</div>',
      '      <div class="amp-selected-formatted" id="ampFormattedAddr">—</div>',
      '      <div class="amp-coords" id="ampCoords"></div>',
      '      <div class="amp-extra-wrap">',
      '        <div class="amp-extra-title">Additional details</div>',
      '        <div class="amp-extra-desc">Correct the house number if inaccurate, or add floor, unit, landmark, etc.</div>',
      '        <input id="ampExtraInput" class="amp-extra-input" type="text" placeholder="e.g. No. 25A, Floor 3, near the main lobby"/>',
      '      </div>',
      '      <button class="amp-btn amp-btn--primary amp-btn--full" id="ampSaveBtn">',
      '        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>',
      '        Save Address',
      '      </button>',
      '    </div>',
      '  </div>',

      '  <div class="amp-toast" id="ampToast"></div>',
      '</div>'
    ].join('\n');
  }

  /* ── ENSURE MODAL IN DOM ────────────────────── */
  function ensureModal() {
    if (qs('#ampOverlay')) return;
    var wrap = document.createElement('div');
    wrap.innerHTML = createModalHTML();
    document.body.appendChild(wrap.firstElementChild);
  }

  /* ── SHOW / HIDE ────────────────────────────── */
  function openModal() {
    editMode = false;
    editAddressId = null;
    ensureModal();
    lockScroll();
    var overlay = qs('#ampOverlay');
    overlay.classList.add('amp-overlay--open');
    showStep('step1');
    bindEvents();
  }

  // Open the picker to EDIT an existing saved address. Jumps straight to the
  // map step centred on the address text (geocoded) so the user can re-pin.
  function openEdit(addressId, addr) {
    editMode = true;
    editAddressId = addressId;
    ensureModal();
    lockScroll();
    var overlay = qs('#ampOverlay');
    overlay.classList.add('amp-overlay--open');
    bindEvents();
    showStep('step2');
    var extraInput = qs('#ampExtraInput');
    if (extraInput) extraInput.value = (addr && addr.extra) || '';
    var searchInput = qs('#ampSearchInput');
    var seed = addr && [addr.address1, addr.city, addr.province, addr.zip, addr.country]
      .filter(Boolean).join(', ');
    loadGoogleMaps().then(function () {
      if (seed && geocoder) {
        geocoder.geocode({ address: seed }, function (results, status) {
          if (status === 'OK' && results[0]) {
            var loc = results[0].geometry.location;
            var ll = { lat: loc.lat(), lng: loc.lng() };
            if (map) { map.setCenter(ll); map.setZoom(DEFAULT_ZOOM); }
            placeMarker(ll);
            updateAddressDisplay(results[0].formatted_address, ll, results[0].address_components);
          }
        });
      }
      if (searchInput && seed) searchInput.value = seed;
    });
  }

  function closeModal() {
    var overlay = qs('#ampOverlay');
    if (!overlay) return;
    overlay.classList.remove('amp-overlay--open');
    unlockScroll();
    // Remove URL param cleanly without reload
    var url = new URL(window.location.href);
    if (url.searchParams.has(URL_PARAM)) {
      url.searchParams.delete(URL_PARAM);
      history.replaceState(null, '', url.toString());
    }
  }

  function showStep(name, center, zoom) {
    var s1 = qs('#ampStep1');
    var s2 = qs('#ampStep2');
    if (!s1 || !s2) return;
    if (name === 'step1') { s1.hidden = false; s2.hidden = true; }
    if (name === 'step2') { s1.hidden = true;  s2.hidden = false; initMapIfNeeded(center || DEFAULT_CENTER, zoom); }
  }

  /* ── MAP INIT ───────────────────────────────── */
  function initMapIfNeeded(center, zoom) {
    zoom = zoom || DEFAULT_ZOOM;
    if (map) {
      // Already initialized — just re-center and update address
      map.setCenter(center);
      map.setZoom(zoom);
      placeMarker(center);
      reverseGeocode(center.lat, center.lng);
      return;
    }
    loadGoogleMaps().then(function () {
      var canvas = qs('#ampMapCanvas');
      if (!canvas) return;

      geocoder = new google.maps.Geocoder();

      map = new google.maps.Map(canvas, {
        center:            center,
        zoom:              zoom,
        disableDefaultUI:  false,
        zoomControl:       true,
        mapTypeControl:    false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling:   'greedy',
        styles: [
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] }
        ]
      });

      placeMarker(center);
      reverseGeocode(center.lat, center.lng);

      // Click on map to move pin (drag/pan does NOT move the pin)
      map.addListener('click', function (e) {
        var ll = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        placeMarker(ll);
        reverseGeocode(ll.lat, ll.lng);
      });

      // Autocomplete search with custom-styled dropdown
      var input = qs('#ampSearchInput');
      if (input) {
        var svc = new google.maps.places.AutocompleteService();
        var placesSession = new google.maps.places.AutocompleteSessionToken();
        var dropdown = qs('#ampAutocompleteList');
        var searchTimer = null;

        function clearDropdown() {
          if (dropdown) { dropdown.innerHTML = ''; dropdown.hidden = true; }
        }

        function selectPrediction(placeId) {
          clearDropdown();
          var ps = new google.maps.places.PlacesService(map);
          ps.getDetails({
            placeId:  placeId,
            fields:   ['geometry', 'formatted_address', 'address_components'],
            sessionToken: placesSession
          }, function (place, status) {
            if (status !== google.maps.places.PlacesServiceStatus.OK || !place.geometry) return;
            placesSession = new google.maps.places.AutocompleteSessionToken();
            var ll = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            };
            input.value = place.formatted_address || '';
            map.setCenter(ll);
            map.setZoom(DEFAULT_ZOOM);
            placeMarker(ll);
            updateAddressDisplay(place.formatted_address, ll, place.address_components);
          });
        }

        input.addEventListener('input', function () {
          clearTimeout(searchTimer);
          var val = input.value.trim();
          if (!val) { clearDropdown(); return; }
          searchTimer = setTimeout(function () {
            svc.getPlacePredictions({
              input:                val,
              sessionToken:         placesSession,
              componentRestrictions: { country: 'id' }
            }, function (predictions, status) {
              clearDropdown();
              if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) return;
              dropdown.hidden = false;
              predictions.forEach(function (pred) {
                var item = document.createElement('div');
                item.className = 'amp-ac-item';
                // Highlight matched substring
                var mainText = pred.structured_formatting.main_text || pred.description;
                var subText  = pred.structured_formatting.secondary_text || '';
                var matched  = pred.structured_formatting.main_text_matched_substrings || [];
                var highlighted = mainText;
                if (matched.length) {
                  var m = matched[0];
                  highlighted = mainText.slice(0, m.offset)
                    + '<mark>' + mainText.slice(m.offset, m.offset + m.length) + '</mark>'
                    + mainText.slice(m.offset + m.length);
                }
                item.innerHTML = [
                  '<span class="amp-ac-pin">',
                    '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">',
                      '<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>',
                      '<circle cx="12" cy="9" r="2.5" fill="white"/>',
                    '</svg>',
                  '</span>',
                  '<span class="amp-ac-text">',
                    '<span class="amp-ac-main">' + highlighted + '</span>',
                    subText ? '<span class="amp-ac-sub">' + subText + '</span>' : '',
                  '</span>'
                ].join('');
                item.addEventListener('mousedown', function (e) {
                  e.preventDefault(); // keep focus on input
                  selectPrediction(pred.place_id);
                });
                dropdown.appendChild(item);
              });
            });
          }, 280);
        });

        input.addEventListener('keydown', function (e) {
          var items = dropdown ? dropdown.querySelectorAll('.amp-ac-item') : [];
          var active = dropdown ? dropdown.querySelector('.amp-ac-item--active') : null;
          var idx = -1;
          items.forEach(function (it, i) { if (it === active) idx = i; });
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (active) active.classList.remove('amp-ac-item--active');
            var next = items[idx + 1] || items[0];
            if (next) next.classList.add('amp-ac-item--active');
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (active) active.classList.remove('amp-ac-item--active');
            var prev = items[idx - 1] || items[items.length - 1];
            if (prev) prev.classList.add('amp-ac-item--active');
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (active) active.dispatchEvent(new MouseEvent('mousedown'));
          } else if (e.key === 'Escape') {
            clearDropdown();
          }
        });

        input.addEventListener('blur', function () {
          setTimeout(clearDropdown, 150);
        });
      }
    }).catch(function (err) {
      console.error('[AMP] Failed to load Google Maps:', err);
      showToast('Failed to load map. Please try again.', 'error');
    });
  }

  /* ── PLACE MARKER ───────────────────────────── */
  function placeMarker(ll) {
    if (!map) return;
    var latLng = new google.maps.LatLng(ll.lat, ll.lng);
    if (marker) {
      marker.setPosition(latLng);
    } else {
      marker = new google.maps.Marker({
        position: latLng,
        map:      map,
        draggable: true,
        animation: google.maps.Animation.DROP,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: getComputedStyle(document.documentElement)
            .getPropertyValue('--color-foreground').trim() || '#111',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2
        }
      });
      marker.addListener('dragend', function (e) {
        var p = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        map.panTo(e.latLng);
        reverseGeocode(p.lat, p.lng);
      });
    }
    selectedAddress.lat = ll.lat;
    selectedAddress.lng = ll.lng;
    updateCoordsDisplay(ll.lat, ll.lng);
  }

  /* ── REVERSE GEOCODE ────────────────────────── */
  function reverseGeocode(lat, lng) {
    if (!geocoder) return;
    geocoder.geocode({ location: { lat: lat, lng: lng } }, function (results, status) {
      if (status === 'OK' && results[0]) {
        console.log('[AMP] Reverse geocode result:', results);
        var r = results[0];
        updateAddressDisplay(r.formatted_address, { lat: lat, lng: lng }, r.address_components);
      }
    });
  }

  function parseAddressComponents(components) {
    var out = { address1: '', city: '', province: '', zip: '', country: '' };
    if (!components) return out;
    components.forEach(function (c) {
      var t = c.types || [];
      if (t.indexOf('route') >= 0)                               out.address1 += c.long_name + ' ';
      if (t.indexOf('street_number') >= 0)                       out.address1  = c.long_name + ' ' + out.address1;
      if (t.indexOf('locality') >= 0 || t.indexOf('administrative_area_level_2') >= 0) out.city = c.long_name;
      if (t.indexOf('administrative_area_level_1') >= 0)         out.province = c.long_name;
      if (t.indexOf('postal_code') >= 0)                         out.zip      = c.long_name;
      if (t.indexOf('country') >= 0)                             out.country  = c.long_name;
    });
    out.address1 = out.address1.trim();
    return out;
  }

  function updateAddressDisplay(formatted, ll, components) {
    var parsed = parseAddressComponents(components);
    selectedAddress.formatted = formatted;
    selectedAddress.lat       = ll.lat;
    selectedAddress.lng       = ll.lng;
    selectedAddress.address1  = parsed.address1;
    selectedAddress.city      = parsed.city;
    selectedAddress.province  = parsed.province;
    selectedAddress.zip       = parsed.zip;
    selectedAddress.country   = parsed.country;

    var el = qs('#ampFormattedAddr');
    if (el) el.textContent = formatted || '—';
    updateCoordsDisplay(ll.lat, ll.lng);
  }

  function updateCoordsDisplay(lat, lng) {
    var el = qs('#ampCoords');
    if (!el) return;
    if (lat === 0 && lng === 0) { el.textContent = ''; return; }
    el.innerHTML =
      '<span>Lat: <strong>' + lat.toFixed(6) + '</strong></span>' +
      '<span>Lng: <strong>' + lng.toFixed(6) + '</strong></span>';
  }

  /* ── GPS FLOW ───────────────────────────────── */
  function handleGPS() {
    var btn      = qs('#ampGpsBtn');
    var idleEl   = btn && btn.querySelector('.amp-gps-idle');
    var loadEl   = btn && btn.querySelector('.amp-gps-loading');

    // Show loading state on Step 1 while awaiting permission
    if (btn)    btn.disabled    = true;
    if (idleEl) idleEl.hidden   = true;
    if (loadEl) loadEl.hidden   = false;

    getCurrentPosition().then(function (pos) {
      var ll = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      // Permission granted — transition to map step with real coords, zoomed in
      showStep('step2', ll, GPS_ZOOM);
      if (pos.coords.accuracy > 50) {
        showToast('Location found (±' + Math.round(pos.coords.accuracy) + 'm). Drag the pin to fine-tune.', 'info');
      }
    }).catch(function () {
      // Permission denied or error — reset button, show map with fallback
      if (btn)    btn.disabled  = false;
      if (idleEl) idleEl.hidden = false;
      if (loadEl) loadEl.hidden = true;
      showToast('Location access denied. Please search your address manually.', 'error');
      showStep('step2');
      var input = qs('#ampSearchInput');
      if (input) setTimeout(function () { input.focus(); }, 400);
    });
  }

  /* ── MANUAL FLOW ────────────────────────────── */
  function handleManual() {
    showStep('step2');
    loadGoogleMaps().then(function () {
      initMapIfNeeded(DEFAULT_CENTER);
      setTimeout(function () {
        var input = qs('#ampSearchInput');
        if (input) input.focus();
      }, 400);
    });
  }

  /* ── RE-CENTER ──────────────────────────────── */
  function handleRecenter() {
    getCurrentPosition().then(function (pos) {
      var ll = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      if (map) { map.setCenter(ll); map.setZoom(GPS_ZOOM); }
      placeMarker(ll);
      reverseGeocode(ll.lat, ll.lng);
      if (pos.coords.accuracy > 50) {
        showToast('Location found (±' + Math.round(pos.coords.accuracy) + 'm). Drag the pin to fine-tune.', 'info');
      }
    }).catch(function () {
      showToast('Location access denied.', 'error');
    });
  }

  /* ── SAVE ───────────────────────────────────── */
  function handleSave() {
    var extra = (qs('#ampExtraInput') || {}).value || '';
    selectedAddress.extra = extra.trim();

    if (!selectedAddress.lat || !selectedAddress.lng) {
      showToast('Please pin your address on the map first.', 'error');
      return;
    }

    var email = window._ampCustomerEmail || '';
    if (!window._ampCustomerLoggedIn || !email) {
      // Not logged in — bounce to login, then come straight back to the picker.
      showToast('Please log in to save your address.', 'error');
      var loginUrl = window._ampLoginUrl || '/account/login';
      var ret = encodeURIComponent(window.location.pathname + '?' + URL_PARAM + '=1');
      setTimeout(function () {
        window.location.href = loginUrl + '?return_url=' + ret;
      }, 900);
      return;
    }

    // The backend stores the human-readable address; `extra` (house no. / floor /
    // landmark) is appended to address1 server-side.
    var payload = {
      request_from: window._ampRequestFrom || undefined,
      email:        email,
      action:       (editMode && editAddressId) ? 'update' : 'create',
      addressId:    (editMode && editAddressId) ? editAddressId : undefined,
      address: {
        formatted: selectedAddress.formatted,
        lat:       selectedAddress.lat,
        lng:       selectedAddress.lng,
        extra:     selectedAddress.extra,
        // Pass parsed components too so the backend can skip re-parsing.
        address1:  selectedAddress.address1,
        city:      selectedAddress.city,
        province:  selectedAddress.province,
        zip:       selectedAddress.zip,
        country:   selectedAddress.country
      }
    };

    console.log('[AMP] Saving address payload:', payload);

    var saveBtn = qs('#ampSaveBtn');
    if (saveBtn) saveBtn.disabled = true;

    fetch(BACKEND_URL + '/api/address/save', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload)
    })
    .then(function (res) { return res.json(); })
    .then(function (data) {
      if (!data || data.success === false) {
        throw new Error((data && data.error) || 'Save failed');
      }
      console.log('[AMP] Saved:', data);
      showToast('Address saved!', 'success');
      // Reload so the server re-renders the address book + checkout default.
      setTimeout(function () {
        closeModal();
        window.location.reload();
      }, 800);
    })
    .catch(function (err) {
      console.error('[AMP] Save error:', err);
      if (saveBtn) saveBtn.disabled = false;
      showToast('Failed to save address. Please try again.', 'error');
    });
  }

  /* ── BIND EVENTS ────────────────────────────── */
  var _bound = false;
  function bindEvents() {
    if (_bound) return;
    _bound = true;

    document.addEventListener('click', function (e) {
      if (e.target.closest('#ampClose1') || e.target.closest('#ampClose2')) closeModal();
      if (e.target.closest('#ampGpsBtn'))    handleGPS();
      if (e.target.closest('#ampManualBtn')) handleManual();
      if (e.target.closest('#ampRecenterBtn')) handleRecenter();
      if (e.target.closest('#ampSaveBtn'))   handleSave();
      // Click overlay backdrop to close
      if (e.target.id === 'ampOverlay')      closeModal();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });
  }

  /* ── CHECKOUT INTERCEPTOR ───────────────────── */
  // Anywhere the checkout button is clicked (cart page OR cart drawer),
  // if the logged-in customer has no saved address, prevent checkout
  // and open the map picker first.
  function interceptCheckout(e) {
    var noAddr   = (window._ampCustomerAddressCount || 0) < 1;
    var loggedIn = window._ampCustomerLoggedIn === true;

    if (!loggedIn || !noAddr) return;

    var t = e.target;
    var hit = t && (
      t.closest('[name="checkout"]') ||
      t.closest('#checkout') ||
      t.closest('.cart__checkout-button') ||
      t.closest('[data-checkout]') ||
      t.closest('form[action="/checkout"] [type="submit"]')
    );

    if (!hit) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    openModal();
  }

  // Capturing phase — fires before Shopify's own handlers
  document.addEventListener('click', interceptCheckout, true);

  /* ── URL PARAM DETECTION ────────────────────── */
  function checkUrlParam() {
    var url    = new URL(window.location.href);
    var val    = url.searchParams.get(URL_PARAM);
    if (val === '1' || val === 'true' || val === '') {
      openModal();
    }
  }

  /* ── PUBLIC API ─────────────────────────────── */
  window._ampOpen     = openModal;
  window._ampOpenAdd  = openModal;
  window._ampOpenEdit = openEdit;
  window._ampClose    = closeModal;

  /* ── BOOTSTRAP ──────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkUrlParam);
  } else {
    checkUrlParam();
  }

})();
